import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    q: 'How long does it take to get set up?',
    a: 'Most organizations are fully configured within a few hours. Create your account, set up membership tiers, import your member data via CSV, and you are live. No consultants or multi-month implementation projects required.',
  },
  {
    q: 'Can I import my existing member data?',
    a: 'Yes. TheOne AMS supports CSV import for members, contacts, and financial records. Upload your spreadsheet, map the columns, and your data is in the system. We handle deduplication and validation automatically.',
  },
  {
    q: 'Does it work for all types of associations?',
    a: 'TheOne AMS supports professional associations, trade associations, HOAs, chambers of commerce, advocacy organizations, and nonprofits. Each vertical (HOA, Chamber, Advocacy) has dedicated features, and the core platform covers member management, events, finance, LMS, and communications for any organization type.',
  },
  {
    q: 'How do online payments and dues work?',
    a: 'Members can pay dues and event registrations online via credit card or ACH. TheOne AMS generates invoices, sends automated reminders before dues are due, and tracks all payments. You get financial reports broken down by membership tier, event, and time period.',
  },
  {
    q: 'Can members log in and manage their own profiles?',
    a: 'Yes. Members get a self-service portal where they can update their profile, pay dues, register for events, access learning content, and participate in community discussions. You control what they can see and edit.',
  },
  {
    q: 'Is there a limit on the number of staff users?',
    a: 'No. All plans include unlimited staff users with role-based permissions. Admins, editors, and viewers each see only what they need. Board members can have read-only access to governance documents.',
  },
  {
    q: 'What about data security?',
    a: 'TheOne AMS runs on enterprise Azure infrastructure with encryption at rest and in transit, MFA support, and SOC 2-aligned security practices. Your data is backed up continuously and isolated per organization.',
  },
  {
    q: 'Can I use TheOne AMS alongside The One Stack?',
    a: 'Yes. TheOne AMS works as a standalone product at theonestack.com, or it integrates with The One Stack Hub for organizations that use multiple One Stack products. Single sign-on, unified billing, and cross-product data sharing are all supported.',
  },
];

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-28 sm:py-32">
      <div className="mx-auto max-w-3xl px-6">
        <div className="mb-16 text-center reveal">
          <span className="mb-4 inline-block rounded-full border border-ams-700/40 bg-ams-950/40 px-3 py-1 text-xs font-medium text-ams-400">
            FAQ
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="reveal space-y-2">
          {faqs.map((faq, i) => (
            <div
              key={faq.q}
              className="rounded-xl border border-ams-800/30 bg-ams-950/20 overflow-hidden"
            >
              <button
                className="flex w-full items-center justify-between px-6 py-4 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="text-sm font-medium text-zinc-200 pr-4">{faq.q}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-zinc-500 transition-transform ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div className={`faq-answer ${openIndex === i ? 'open' : ''}`}>
                <div className="px-6 pb-4">
                  <p className="text-sm leading-relaxed text-zinc-400">{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
